import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CloseIcon } from './icons/CloseIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { FilmIcon } from './icons/FilmIcon';
import { PlayIcon } from './icons/PlayIcon';
import { CheckIcon } from './icons/CheckIcon';
import { generateSceneImage, generateFramePromptsFromDescription } from '../services/geminiService';
import { generateVideo, generateVideoProductionPrompt } from '../services/aiService';
import { startCloudRender, pollRenderUntilComplete, saveProjectToBackend } from '../services/backendService';
import { useUser } from '../context/UserContext';

/**
 * AutoGenerateVideoModal - Automated video generation workflow
 * 
 * This modal automates the entire video creation process:
 * 1. Generate frame images for all shots (start + end frames)
 * 2. Generate videos for all shots using the frames
 * 3. Save project after each generation
 * 4. Render final video using cloud render
 * 5. Display the final video
 */
export const AutoGenerateVideoModal = ({
    projectData,
    onProjectUpdate,
    onClose,
    onTrackCreditsUsed,
    onSaveProject,
    onNavigateToStudio
}) => {
    const { user } = useUser();
    const [status, setStatus] = useState('idle'); // idle, generating-frames, generating-videos, rendering, done, error
    const [currentStep, setCurrentStep] = useState('');
    const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });
    const [error, setError] = useState(null);
    const [finalVideoUrl, setFinalVideoUrl] = useState(null);
    const [logs, setLogs] = useState([]);
    const [generatedProject, setGeneratedProject] = useState(null);
    
    const abortRef = useRef(false);
    const logsEndRef = useRef(null);

    // Auto-scroll logs
    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    const addLog = useCallback((message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { timestamp, message, type }]);
    }, []);

    const userEmail = user?.email || 
                      (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null) || 
                      'guest@loopai.com';

    // Get all shots from all scenes
    const getAllShots = useCallback(() => {
        const shots = [];
        projectData.scenes?.forEach((scene, sceneIdx) => {
            scene.shots?.forEach((shot, shotIdx) => {
                shots.push({
                    ...shot,
                    sceneId: scene.id,
                    sceneIndex: sceneIdx,
                    shotIndex: shotIdx,
                    displayName: `Scene ${sceneIdx + 1}, Shot ${shotIdx + 1}`
                });
            });
        });
        return shots;
    }, [projectData.scenes]);

    // Get characters and elements for a shot
    const getEntitiesForShot = useCallback((shot) => {
        const characters = projectData.characters?.filter(c => 
            shot.characterIds?.includes(c.id) && (c.category === 'character' || c.category === 'narrator' || !c.category)
        ) || [];
        
        const elements = projectData.characters?.filter(c => 
            shot.elementIds?.includes(c.id) && c.category === 'element'
        ) || [];
        
        // Smart match elements mentioned in description
        const descLower = (shot.description || '').toLowerCase();
        const smartMatchedElements = projectData.characters?.filter(c => 
            c.category === 'element' && 
            !shot.elementIds?.includes(c.id) &&
            c.name && descLower.includes(c.name.toLowerCase())
        ) || [];
        
        return [...characters, ...elements, ...smartMatchedElements];
    }, [projectData.characters]);

    // Generate frame prompts if missing
    const ensureFramePrompts = async (shot) => {
        if (!shot.startFramePrompt || !shot.endFramePrompt) {
            if (shot.description) {
                addLog(`Generating frame prompts for ${shot.displayName}...`);
                const { startFramePrompt, endFramePrompt } = await generateFramePromptsFromDescription(shot.description);
                return { ...shot, startFramePrompt, endFramePrompt };
            }
        }
        return shot;
    };

    // Generate a single frame image
    const generateFrameImage = async (shot, frameType, entities) => {
        const prompt = frameType === 'start' ? shot.startFramePrompt : shot.endFramePrompt;
        if (!prompt) {
            throw new Error(`No ${frameType} frame prompt for ${shot.displayName}`);
        }

        const refs = [];
        
        // For end frame, include start frame for continuity
        if (frameType === 'end' && shot.startFrameImage) {
            refs.push(shot.startFrameImage);
        }
        
        // Add element reference images
        entities.forEach(entity => {
            if (entity.image) {
                refs.push(entity.image);
            }
        });

        const result = await generateSceneImage(prompt, projectData.aspectRatio, entities, refs);
        const image = result.image || result.images?.[0] || result;

        // Track credits
        if (onTrackCreditsUsed && result.creditsUsed > 0) {
            onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
        }

        return image;
    };

    // Generate video for a shot
    const generateShotVideo = async (shot, entities) => {
        // Get video production prompt from backend
        let videoPrompt = shot.description || '';
        let dialoguesData = [];
        let cinematicActions = [];

        try {
            // Try to get enhanced video production prompt
            const productionPrompt = await generateVideoProductionPrompt({
                shotDescription: shot.description,
                characterName: entities[0]?.name || 'Character',
                sceneContext: shot.description,
                durationSeconds: 5,
                aspectRatio: projectData.aspectRatio,
                mood: projectData.mood || 'cinematic',
                genre: projectData.genre || 'drama'
            });

            if (productionPrompt) {
                videoPrompt = productionPrompt.video_prompt?.main_prompt || 
                             productionPrompt.video_prompt?.complete_prompt ||
                             videoPrompt;
                cinematicActions = productionPrompt.character_actions || [];
            }
        } catch (e) {
            addLog(`Using basic prompt for video generation`, 'warning');
        }

        // Build dialogues data
        if (shot.dialogues && shot.dialogues.length > 0) {
            dialoguesData = shot.dialogues.map(d => {
                const char = projectData.characters?.find(c => c.id === d.characterId);
                return {
                    character_name: char?.name || 'Unknown',
                    text: d.text,
                    voice_id: char?.elevenLabsVoiceId
                };
            }).filter(d => d.text);
        }

        // Add narration if present
        if (shot.narration) {
            const narrator = projectData.characters?.find(c => c.category === 'narrator');
            dialoguesData.push({
                character_name: narrator?.name || 'Narrator',
                text: shot.narration,
                voice_id: narrator?.elevenLabsVoiceId,
                is_narration: true
            });
        }

        // Get video model from localStorage
        let selectedModels = {};
        try {
            const stored = localStorage.getItem('selectedAIModels');
            if (stored) {
                selectedModels = JSON.parse(stored);
            }
        } catch (e) {
            // Ignore JSON parse errors
        }

        const hasBothFrames = shot.startFrameImage && shot.endFrameImage;
        let modelKey;
        
        if (hasBothFrames) {
            modelKey = selectedModels['start-end-frame videos'] || 
                       selectedModels['start-end-frame video'] ||
                       'kling-2.5-turbo';
        } else {
            modelKey = selectedModels['Image to video'] || 
                       selectedModels['image-to-video'] ||
                       'kling-2.5-turbo';
        }

        addLog(`Using model: ${modelKey}`, 'info');

        const result = await generateVideo(
            videoPrompt,
            5, // duration
            modelKey,
            projectData.aspectRatio,
            shot.startFrameImage,
            shot.endFrameImage,
            (progressMsg) => addLog(progressMsg, 'progress'),
            dialoguesData.length > 0 ? dialoguesData : null,
            cinematicActions.length > 0 ? cinematicActions : null
        );

        // Track credits
        if (onTrackCreditsUsed && result.creditsUsed > 0) {
            onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
        }

        return {
            videoUrl: result.videoUrl,
            videoDuration: 5 // Will be updated when video loads
        };
    };

    // Update shot in project
    const updateShotInProject = (project, sceneId, shotId, updates) => {
        return {
            ...project,
            scenes: project.scenes.map(scene => {
                if (scene.id === sceneId) {
                    return {
                        ...scene,
                        shots: scene.shots.map(shot => {
                            if (shot.id === shotId) {
                                return { ...shot, ...updates };
                            }
                            return shot;
                        })
                    };
                }
                return scene;
            }),
            lastModified: Date.now()
        };
    };

    // Create timeline from scenes
    const createTimelineFromScenes = async (scenes) => {
        const tracks = [
            { id: 'track-video', type: 'video', name: 'Video' },
            { id: 'track-audio-music', type: 'audio', name: 'Music' },
            { id: 'track-audio-sfx', type: 'audio', name: 'SFX' },
            { id: 'track-text', type: 'text', name: 'Text' }
        ];
        
        const clips = [];
        let currentTime = 0;
        const videoTrackId = 'track-video';
        let clipIndex = 0;

        for (let sceneIdx = 0; sceneIdx < scenes.length; sceneIdx++) {
            const scene = scenes[sceneIdx];
            for (let shotIdx = 0; shotIdx < (scene.shots || []).length; shotIdx++) {
                const shot = scene.shots[shotIdx];
                if (shot.video) {
                    const duration = shot.videoDuration || 5;
                    // Use a unique ID combining scene index, shot index, and clip index
                    const uniqueId = shot.id || `scene${sceneIdx}-shot${shotIdx}-${Date.now()}-${clipIndex}`;
                    clips.push({
                        id: `clip-video-${uniqueId}`,
                        assetId: 'none',
                        shotId: shot.id || uniqueId,
                        sceneIndex: sceneIdx,
                        shotIndex: shotIdx,
                        type: 'video',
                        subtype: 'video',
                        trackId: videoTrackId,
                        start: currentTime,
                        duration: duration,
                        sourceStart: 0,
                        sourceDuration: duration,
                        transition: { type: 'none', duration: 0 }
                    });
                    currentTime += duration;
                    clipIndex++;
                }
            }
        }

        return {
            tracks,
            clips,
            duration: Math.max(10, currentTime + 1)
        };
    };

    // Main generation workflow
    const startGeneration = async () => {
        setStatus('generating-frames');
        setError(null);
        setLogs([]);
        abortRef.current = false;

        let currentProject = { ...projectData };
        const allShots = getAllShots();
        const totalShots = allShots.length;

        addLog(`Starting automated video generation for ${totalShots} shots...`, 'info');
        addLog(`Project: ${currentProject.name}`, 'info');

        try {
            // PHASE 1: Generate frame images
            setProgress({ current: 0, total: totalShots * 2, phase: 'Generating Frame Images' });
            addLog('=== PHASE 1: Generating Frame Images ===', 'phase');

            for (let i = 0; i < allShots.length; i++) {
                if (abortRef.current) {
                    throw new Error('Generation cancelled by user');
                }

                let shot = allShots[i];
                const entities = getEntitiesForShot(shot);
                
                // Ensure frame prompts exist
                shot = await ensureFramePrompts(shot);
                currentProject = updateShotInProject(currentProject, shot.sceneId, shot.id, {
                    startFramePrompt: shot.startFramePrompt,
                    endFramePrompt: shot.endFramePrompt
                });

                // Generate start frame if missing
                if (!shot.startFrameImage && shot.startFramePrompt) {
                    setCurrentStep(`Generating start frame for ${shot.displayName}`);
                    addLog(`Generating start frame for ${shot.displayName}...`);
                    
                    try {
                        const startImage = await generateFrameImage(shot, 'start', entities);
                        shot.startFrameImage = startImage;
                        currentProject = updateShotInProject(currentProject, shot.sceneId, shot.id, {
                            startFrameImage: startImage
                        });
                        
                        // Save project after each generation
                        onProjectUpdate(currentProject);
                        await saveProjectToBackend(currentProject, userEmail);
                        addLog(`✓ Start frame generated and saved`, 'success');
                    } catch (err) {
                        addLog(`✗ Failed to generate start frame: ${err.message}`, 'error');
                        throw err;
                    }
                } else if (shot.startFrameImage) {
                    addLog(`Start frame already exists for ${shot.displayName}`, 'skip');
                }

                setProgress(prev => ({ ...prev, current: i * 2 + 1 }));

                // Generate end frame if missing
                if (!shot.endFrameImage && shot.endFramePrompt) {
                    setCurrentStep(`Generating end frame for ${shot.displayName}`);
                    addLog(`Generating end frame for ${shot.displayName}...`);
                    
                    try {
                        const endImage = await generateFrameImage(shot, 'end', entities);
                        shot.endFrameImage = endImage;
                        currentProject = updateShotInProject(currentProject, shot.sceneId, shot.id, {
                            endFrameImage: endImage
                        });
                        
                        // Save project after each generation
                        onProjectUpdate(currentProject);
                        await saveProjectToBackend(currentProject, userEmail);
                        addLog(`✓ End frame generated and saved`, 'success');
                    } catch (err) {
                        addLog(`✗ Failed to generate end frame: ${err.message}`, 'error');
                        throw err;
                    }
                } else if (shot.endFrameImage) {
                    addLog(`End frame already exists for ${shot.displayName}`, 'skip');
                }

                setProgress(prev => ({ ...prev, current: i * 2 + 2 }));
            }

            // PHASE 2: Generate videos
            setStatus('generating-videos');
            setProgress({ current: 0, total: totalShots, phase: 'Generating Videos' });
            addLog('=== PHASE 2: Generating Videos ===', 'phase');

            // Re-fetch shots with updated images
            const updatedShots = [];
            currentProject.scenes?.forEach((scene, sceneIdx) => {
                scene.shots?.forEach((shot, shotIdx) => {
                    updatedShots.push({
                        ...shot,
                        sceneId: scene.id,
                        sceneIndex: sceneIdx,
                        shotIndex: shotIdx,
                        displayName: `Scene ${sceneIdx + 1}, Shot ${shotIdx + 1}`
                    });
                });
            });

            for (let i = 0; i < updatedShots.length; i++) {
                if (abortRef.current) {
                    throw new Error('Generation cancelled by user');
                }

                const shot = updatedShots[i];
                const entities = getEntitiesForShot(shot);

                if (!shot.video && shot.startFrameImage) {
                    setCurrentStep(`Generating video for ${shot.displayName}`);
                    addLog(`Generating video for ${shot.displayName}...`);
                    
                    try {
                        const videoResult = await generateShotVideo(shot, entities);
                        currentProject = updateShotInProject(currentProject, shot.sceneId, shot.id, {
                            video: videoResult.videoUrl,
                            videoDuration: videoResult.videoDuration
                        });
                        
                        // Save project after each video generation
                        onProjectUpdate(currentProject);
                        await saveProjectToBackend(currentProject, userEmail);
                        addLog(`✓ Video generated and saved`, 'success');
                    } catch (err) {
                        addLog(`✗ Failed to generate video: ${err.message}`, 'error');
                        // Continue with other shots even if one fails
                        addLog(`Continuing with remaining shots...`, 'warning');
                    }
                } else if (shot.video) {
                    addLog(`Video already exists for ${shot.displayName}`, 'skip');
                } else {
                    addLog(`Skipping ${shot.displayName} - no start frame available`, 'skip');
                }

                setProgress(prev => ({ ...prev, current: i + 1 }));
            }

            // PHASE 3: Create timeline and render
            setStatus('rendering');
            setProgress({ current: 0, total: 100, phase: 'Rendering Final Video' });
            addLog('=== PHASE 3: Rendering Final Video ===', 'phase');

            // Create timeline
            addLog('Creating timeline from generated videos...');
            const timeline = await createTimelineFromScenes(currentProject.scenes);
            currentProject = { ...currentProject, timeline, currentStep: 'studio' };
            onProjectUpdate(currentProject);
            
            // Save with timeline
            addLog('Saving project with timeline...');
            const saveResult = await saveProjectToBackend(currentProject, userEmail);
            
            if (!saveResult.success) {
                throw new Error(saveResult.error || 'Failed to save project');
            }

            // Start cloud render
            addLog('Starting cloud render...');
            setCurrentStep('Cloud rendering in progress...');
            
            const projectForRender = saveResult.optimizedProject || currentProject;
            const renderResult = await startCloudRender(projectForRender, userEmail);
            
            if (!renderResult.success || !renderResult.jobId) {
                throw new Error(renderResult.error || 'Failed to start cloud render');
            }

            addLog(`Render job started: ${renderResult.jobId}`);

            // Poll for render completion
            const pollResult = await pollRenderUntilComplete(
                renderResult.jobId,
                (statusUpdate) => {
                    if (statusUpdate.status === 'processing') {
                        setCurrentStep('Cloud server is rendering your video...');
                        addLog('Render in progress...', 'progress');
                    }
                },
                600 // 10 minute timeout
            );

            if (pollResult.success && pollResult.url) {
                setFinalVideoUrl(pollResult.url);
                
                // Save final video URL to project with completed status
                // This enables "View Video" functionality in the User Dashboard
                currentProject = { 
                    ...currentProject, 
                    renderedVideoUrl: pollResult.url,
                    video_url: pollResult.url,
                    status: 'completed',
                    lastModified: Date.now()
                };
                onProjectUpdate(currentProject);
                
                addLog('Saving completed project with video URL...', 'info');
                const saveResult = await saveProjectToBackend(currentProject, userEmail);
                if (saveResult.success) {
                    addLog('✓ Project saved with video URL', 'success');
                } else {
                    addLog('⚠ Warning: Could not save to backend: ' + (saveResult.error || 'Unknown error'), 'warning');
                }
                
                setGeneratedProject(currentProject);
                setStatus('done');
                addLog('=== VIDEO GENERATION COMPLETE ===', 'phase');
                addLog(`Final video URL: ${pollResult.url}`, 'success');
            } else {
                throw new Error(pollResult.error || 'Cloud rendering failed');
            }

        } catch (err) {
            console.error('Auto generation error:', err);
            setError(err.message || 'An error occurred during generation');
            setStatus('error');
            addLog(`ERROR: ${err.message}`, 'error');
            
            // Save whatever progress we made
            if (currentProject) {
                setGeneratedProject(currentProject);
                onProjectUpdate(currentProject);
                try {
                    await saveProjectToBackend(currentProject, userEmail);
                    addLog('Progress saved despite error', 'info');
                } catch (saveErr) {
                    // Ignore save errors during error recovery
                    console.warn('Failed to save progress:', saveErr);
                }
            }
        }
    };

    const handleCancel = () => {
        abortRef.current = true;
        addLog('Cancelling generation...', 'warning');
    };

    const handleGoToStudio = () => {
        if (generatedProject && onNavigateToStudio) {
            onNavigateToStudio(generatedProject);
        }
        onClose();
    };

    const getProgressPercent = () => {
        if (progress.total === 0) return 0;
        return Math.round((progress.current / progress.total) * 100);
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <FilmIcon className="w-6 h-6 text-purple-500" />
                        <h2 className="text-xl font-bold text-white">Automated Video Generation</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                        disabled={status === 'generating-frames' || status === 'generating-videos' || status === 'rendering'}
                    >
                        <CloseIcon className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Status Display */}
                    {status === 'idle' && (
                        <div className="text-center py-8">
                            <FilmIcon className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-white mb-2">Ready to Generate</h3>
                            <p className="text-gray-400 mb-6 max-w-md mx-auto">
                                This will automatically generate frame images and videos for all {getAllShots().length} shots, 
                                then render the final video.
                            </p>
                            <div className="bg-gray-800/50 rounded-lg p-4 text-left max-w-md mx-auto mb-6">
                                <h4 className="text-sm font-semibold text-purple-400 mb-2">Process Overview:</h4>
                                <ol className="text-sm text-gray-300 space-y-1">
                                    <li>1. Generate start & end frame images for each shot</li>
                                    <li>2. Generate videos using the frames, dialogues & narration</li>
                                    <li>3. Save project after each generation</li>
                                    <li>4. Render final video using cloud render</li>
                                    <li>5. Display and save the final video</li>
                                </ol>
                            </div>
                            <button
                                onClick={startGeneration}
                                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg transition-colors flex items-center gap-2 mx-auto"
                            >
                                <PlayIcon className="w-5 h-5" />
                                Start Generation
                            </button>
                        </div>
                    )}

                    {/* Progress Display */}
                    {(status === 'generating-frames' || status === 'generating-videos' || status === 'rendering') && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <LoadingSpinner className="w-6 h-6 text-purple-500" />
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{progress.phase}</h3>
                                    <p className="text-sm text-gray-400">{currentStep}</p>
                                </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="w-full bg-gray-700 rounded-full h-3">
                                <div 
                                    className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all duration-300"
                                    style={{ width: `${getProgressPercent()}%` }}
                                />
                            </div>
                            <div className="text-center text-sm text-gray-400">
                                {progress.current} / {progress.total} ({getProgressPercent()}%)
                            </div>

                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                    {/* Done Display */}
                    {status === 'done' && finalVideoUrl && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-green-400">
                                <CheckIcon className="w-6 h-6" />
                                <h3 className="text-lg font-semibold">Video Generation Complete!</h3>
                            </div>
                            
                            {/* Video Preview */}
                            <div className="bg-black rounded-lg overflow-hidden">
                                <video 
                                    src={finalVideoUrl} 
                                    controls 
                                    className="w-full max-h-[400px]"
                                    autoPlay
                                />
                            </div>

                            <div className="flex gap-3 justify-center">
                                <a
                                    href={finalVideoUrl}
                                    download={`${projectData.name || 'video'}_final.mp4`}
                                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download Video
                                </a>
                                <button
                                    onClick={handleGoToStudio}
                                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    Go to Studio Editor
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {status === 'error' && (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-400">
                                <h3 className="font-semibold mb-2">Generation Error</h3>
                                <p>{error}</p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={startGeneration}
                                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                    Retry
                                </button>
                                {generatedProject && (
                                    <button
                                        onClick={handleGoToStudio}
                                        className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                    >
                                        Continue in Studio (with progress)
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Logs */}
                    {logs.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Generation Log:</h4>
                            <div className="bg-gray-800/50 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs">
                                {logs.map((log, idx) => (
                                    <div 
                                        key={idx} 
                                        className={`py-0.5 ${
                                            log.type === 'error' ? 'text-red-400' :
                                            log.type === 'success' ? 'text-green-400' :
                                            log.type === 'warning' ? 'text-yellow-400' :
                                            log.type === 'phase' ? 'text-purple-400 font-bold mt-2' :
                                            log.type === 'skip' ? 'text-gray-500' :
                                            log.type === 'progress' ? 'text-blue-400' :
                                            'text-gray-300'
                                        }`}
                                    >
                                        <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                                    </div>
                                ))}
                                <div ref={logsEndRef} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-700 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        disabled={status === 'generating-frames' || status === 'generating-videos' || status === 'rendering'}
                    >
                        {status === 'done' ? 'Close' : 'Cancel'}
                    </button>
                </div>
            </div>
        </div>
    );
};
